import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
// helpers
import { navigateBackOrToMain } from "../../../../../common/utils/helpers/navigateBackOrToMain";
import { shouldShowMilesAndMore } from "../../../../../common/utils/transport/airlineBookingPayload";
// hooks
import { useActions } from "../../../../../common/hooks/useActions";
// actions
import { swissTravelPassActionCreators } from "../swissTravelPassModule";
// components
import AppFormInput from "../../../../../components/form/AppFormInputs/AppFormInput";
import AppButton from "../../../../../components/ui/AppButton";
import MilesAndMoreInfo from "../../../../../components/MilesAndMoreInfo";
import TransportCancellationPolicy from "../../../../../components/TransportProviderContent/TransportCancellationPolicy";

interface SwissTravelPassSummaryTypes {
  onBuyTicket: () => void;
  comment: string;
  setComment: (comment: string) => void;
  selectedTravelPassOffer: any;
  cancellationPolicy?: string | null;
}

export default function SwissTravelPassSummary({
  onBuyTicket,
  comment,
  setComment,
  selectedTravelPassOffer,
  cancellationPolicy
}: SwissTravelPassSummaryTypes) {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const language = localStorage.getItem("i18nextLng") || "en";

  // states
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const travelPassPrebookingInfoResponse = useSelector(
    (state: any) => state.swissTravelPass.travelPassPrebookingInfoResponse
  );
  const travelPassPassengerList = useSelector((state: any) => state.swissTravelPass.travelPassPassengerList);
  const platformType = useSelector((state: any) => state.global.platformType);
  // actions
  const resetSwissTravelPrebookState = useActions(
    swissTravelPassActionCreators?.resetSwissTravelPrebookStateAction,
    []
  );

  // helpers
  const isAirline = platformType === "airline";

  const handleComment = (e: any) => {
    setComment(e.target.value);
  };

  const handeEditTicketOptionButton = () => {
    if (window.confirm(t("transport.editOptionText"))) {
      resetSwissTravelPrebookState();
    }
  };

  return (
    travelPassPrebookingInfoResponse && (
      <Box sx={{ display: "flex", flexDirection: "column" }} pb={3} pt={3}>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between" }}>
            <Box>
              <Box className="summary-option-title" mb={2}>
                {t("transport.ticketOption")}
              </Box>
              <Box className="summary-option-title default-text-transform">{selectedTravelPassOffer?.name}</Box>
            </Box>
            <Grid item sx={{ width: "auto", height: "40px" }} mt={{ xs: 1.5, sm: 0 }}>
              <AppButton
                label={t("transport.editBtn")}
                onClick={handeEditTicketOptionButton}
                disableRipple={true}
                customBackgroundColor={theme.palette.primary.dark}
                customHoverColor={theme.palette.primary.dark}
                fontSize="14px"
              />
            </Grid>
          </Box>
          <Grid container className="summary-passenger-box" mt={2} mb={2} pb={2} pt={2}>
            {travelPassPassengerList?.length > 0 &&
              travelPassPassengerList?.map((passenger: any) => {
                return (
                  <Box
                    key={Math.random()}
                    sx={{ display: "flex", textAlign: "center", alignItems: "center", justifyContent: "center" }}
                  >
                    <Grid item xs={3} pl={{ xs: 1, sm: 0 }}>
                      {passenger?.firstName} {passenger?.lastName}
                    </Grid>
                    <Grid item xs={3}>
                      {JSON.parse(passenger?.countryOfResidence)?.name}
                    </Grid>
                    <Grid item xs={3}>
                      {format(new Date(passenger?.birthDate), "dd.MM.yyyy")}
                    </Grid>
                    <Grid item xs={3}>
                      {travelPassPrebookingInfoResponse?.details?.passengers?.[
                        travelPassPassengerList.indexOf(passenger)
                      ]?.price || ""}{" "}
                      {travelPassPrebookingInfoResponse?.details?.passengers?.[
                        travelPassPassengerList.indexOf(passenger)
                      ]?.currency || ""}
                    </Grid>
                  </Box>
                );
              })}
          </Grid>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center" }} />
            <Box className="summary-total-price-label" mr={{ xs: 0, sm: 3, md: 6 }}>
              {t("transport.totalPrice")}: {travelPassPrebookingInfoResponse?.formatted_price}
            </Box>
          </Box>
        </Box>
        <Grid container mt={4}>
          <Grid item xs={12} md={6} sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            <Grid item xs={3}>
              <Box sx={{ fontSize: { xs: 12, sm: 14 } }}>{t("transport.commentLabel")}</Box>
            </Grid>
            <Grid item xs={9}>
              <AppFormInput name={"commentForm"} type="string" value={comment} onChange={handleComment} />
            </Grid>
          </Grid>
          {/* Right side: Miles & More info (responsive: below on mobile) */}
          {shouldShowMilesAndMore(travelPassPrebookingInfoResponse?.miles_and_more, isAirline) && (
            <Grid item xs={12} md={6} pl={{ xs: 0, md: 5 }} pt={{ xs: 4, md: 0 }}>
              <MilesAndMoreInfo miles={travelPassPrebookingInfoResponse?.miles_and_more} />
            </Grid>
          )}
        </Grid>
        {cancellationPolicy && (
          <Box pt={6}>
            <TransportCancellationPolicy cancellationPolicy={cancellationPolicy} />
          </Box>
        )}
        <Grid
          item
          xs={12}
          sx={{
            width: { xs: "100%", sm: "100%", md: isAirline ? "45%" : "30%" },
            margin: cancellationPolicy ? "24px auto 0 auto" : "60px auto 0 auto",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 2 }
          }}
        >
          {isAirline && (
            <AppButton
              label={t("transport.goBack")}
              onClick={() => navigateBackOrToMain(navigate, { language })}
              disableRipple={true}
              customBackgroundColor={theme.palette.primary.dark}
              customHoverColor={theme.palette.primary.dark}
              fontSize="14px"
            />
          )}
          <AppButton
            label={t("transport.buyTicketsBtn")}
            onClick={onBuyTicket}
            disableRipple={true}
            customBackgroundColor={tenantInfo?.style?.button?.color}
            customActiveColor={tenantInfo?.style?.button?.active}
            customHoverColor={tenantInfo?.style?.button?.hover}
            customClickColor={tenantInfo?.style?.button?.click}
            fontSize="14px"
          />
        </Grid>
      </Box>
    )
  );
}
